import { CompleteKundali, Language } from '../types/kundali';
import { LifeThemeConfig, LifeThemeId, LifeThemeQuestion } from '../types/advancedLifeAnalysis';
import { calculateExactAge } from './lifeStageContextEngine';

/**
 * Calculates exact native age in years from Kundali birth details
 */
export function getNativeAgeInYears(kundali: CompleteKundali, targetDate: Date = new Date()): number {
  const birthDateStr = kundali.birthDetails?.date;
  if (!birthDateStr) return 30; // fallback standard adult age
  const { exactAge } = calculateExactAge(birthDateStr, targetDate);
  return exactAge;
}

/**
 * Desh-Kaal-Patra (Time-Place-Native Context) Theme & Inquiry Eligibility Rules
 *
 * Vedic Ashrama & Biological / Societal Stage mapping:
 * - Age < 18: Focus on Study, Health, Family, Future trajectory. (Hide Marriage, Children, Business, Litigation)
 * - Age 18 - 28: Higher Education, Competitive Exams, Early Career Inception, Marriage (if >= 20).
 * - Age 29 - 40: Career Growth, Marriage, Childbirth & Family, Wealth, Property, Business, Vehicle.
 * - Age 41 - 58 (Consolidation / Mature Grihastha e.g. Navratan at 44):
 *     - Career Peak, Wealth, Real Estate, Business, Health, Family, Spirituality.
 *     - Children domain focuses strictly on Children's Education, Intellect & Career Future (NOT childbirth/conception).
 *     - First Marriage timing is hidden by default from top tabs (unless explicitly asked via AI/Acharya).
 *     - Primary schooling (native's own school/college entrance) is hidden by default (moved to lifelong learning/children's education).
 * - Age > 58: Health, Longevity, Spirituality, Wealth/Legacy, Family/Grandchildren.
 */

export function isThemeAgeAppropriate(
  themeId: LifeThemeId,
  age: number,
  isExplicitInvocation: boolean = false
): boolean {
  // If explicitly requested by user via AI Astrologer or Ask Acharya, all themes are allowed
  if (isExplicitInvocation) return true;

  if (age < 18) {
    if (['marriage_relationship', 'children', 'business', 'litigation_competition', 'vehicle', 'house_property'].includes(themeId)) {
      return false;
    }
    return true;
  }

  if (age >= 18 && age <= 28) {
    // Young adult: allow education, career, wealth, vehicle, health, marriage (if >= 20), children only if >= 21
    if (themeId === 'children' && age < 21) return false;
    return true;
  }

  if (age >= 29 && age <= 38) {
    // Prime family, marriage & career building:
    // Native's own primary schooling is not primary (allow higher learning / business / career / marriage / children / wealth / property / vehicle)
    if (themeId === 'education') return false;
    return true;
  }

  if (age >= 39 && age <= 58) {
    // Mature adults (e.g. Navratan at 44 yrs):
    // 1. Native's own primary schooling/entrance exam ('education') is hidden (not in student age range)
    if (themeId === 'education') return false;
    // 2. Society marriage search ('marriage_relationship') is hidden (outside primary societal first-marriage age)
    if (themeId === 'marriage_relationship') return false;
    // 3. 'children' is KEPT, but its scope is strictly Children's Education & Future (childbirth is removed)
    // 4. Career, Wealth, Property, Vehicle, Business, Health, Family, Spirituality, Travel are all fully accessible!
    return true;
  }

  if (age > 58) {
    // Senior adults / Vanaprastha:
    if (['education', 'marriage_relationship', 'business', 'vehicle'].includes(themeId)) return false;
    return true;
  }

  return true;
}

/**
 * Filter questions within a theme based on Desh-Kaal-Patra native age
 */
export function isQuestionAgeAppropriate(
  question: LifeThemeQuestion,
  age: number,
  isExplicitInvocation: boolean = false
): boolean {
  if (isExplicitInvocation) return true;

  // Children theme questions
  if (question.themeId === 'children') {
    if (question.id === 'children_timing_conception_birth') {
      // Childbirth & conception is only relevant for societal childbearing age (20 to 38)
      return age >= 20 && age <= 38;
    }
    // Children's education and future is relevant for parents age 24+
    if (question.id === 'children_prosperity_education' || question.id === 'children_education_career_support') {
      return age >= 24;
    }
  }

  // Marriage theme questions
  if (question.themeId === 'marriage_relationship') {
    if (question.id === 'marriage_timing_window' || question.id === 'marriage_spouse_characteristics' || question.id === 'marriage_obstacles_remedies') {
      // First marriage timing & spouse finding: societal range (18 to 38)
      return age >= 18 && age <= 38;
    }
    if (question.id === 'marriage_harmony_longevity') {
      // Marital harmony is relevant for any married adult (age >= 22 to 55)
      return age >= 22 && age <= 55;
    }
  }

  // Education theme questions (native's own education)
  if (question.themeId === 'education') {
    if (question.id === 'education_field_stream_selection' || question.id === 'education_competitive_exam_timing') {
      // Primary academic stream & student entrance exams
      return age <= 28;
    }
  }

  return true;
}

/**
 * Returns filtered and age-adapted themes for a native
 */
export function getDeshKaalPatraThemes(
  kundali: CompleteKundali,
  allThemes: LifeThemeConfig[],
  isExplicitInvocation: boolean = false
): LifeThemeConfig[] {
  const age = getNativeAgeInYears(kundali);
  return allThemes.filter((t) => isThemeAgeAppropriate(t.id, age, isExplicitInvocation));
}

/**
 * Returns filtered and age-adapted questions for a given theme
 */
export function getDeshKaalPatraQuestions(
  kundali: CompleteKundali,
  theme: LifeThemeConfig,
  isExplicitInvocation: boolean = false
): LifeThemeQuestion[] {
  const age = getNativeAgeInYears(kundali);
  const filtered = theme.questions.filter((q) => isQuestionAgeAppropriate(q, age, isExplicitInvocation));

  // Fallback: if all questions were filtered out for some reason, return at least the first or adapted question
  if (filtered.length === 0 && theme.questions.length > 0) {
    return [theme.questions[0]];
  }

  return filtered;
}

/**
 * Returns age-adapted display label and description for a theme
 * E.g., for 44-year-old native, 'children' theme displays as "Children's Education & Future"
 */
export function getDeshKaalPatraThemeDisplay(
  theme: LifeThemeConfig,
  age: number,
  lang: Language
): { name: string; description: string } {
  const isHi = lang === 'hi';

  if (theme.id === 'children' && age >= 41) {
    return {
      name: isHi ? 'संतान की विद्या, करियर व भविष्य' : "Children's Education & Future",
      description: isHi
        ? 'संतान की उच्च शिक्षा, विद्या, संस्कार, करियर दिशा एवं उनकी जीवन में उन्नति का शास्त्रीय विश्लेषण'
        : "Vedic analysis for your children's higher education, intellect, career trajectory, and life settlement"
    };
  }

  if (theme.id === 'career_job' && age >= 41) {
    return {
      name: isHi ? 'कार्यक्षेत्र, नेतृत्व एवं सर्वोच्च पद' : 'Career Peak & Executive Authority',
      description: isHi
        ? 'दशम भाव एवं दशमांश चक्र आधारित नेतृत्व, पदोन्नति, कार्यक्षेत्र स्थिरता एवं प्रतिष्ठा का फलादेश'
        : 'Vedic synthesis for executive leadership, professional authority, career consolidation, and long-term status'
    };
  }

  return {
    name: isHi ? theme.nameHi : theme.nameEn,
    description: isHi ? theme.descriptionHi : theme.descriptionEn
  };
}
